import {Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {PackDetailsPageComponent} from '../pack-details-page/pack-details-page.component';
import {IconType} from '../../icons';
import {ButtonComponent} from '../../shared/button/button.component';
import {IconComponent} from '../../shared/icon/icon.component';
import {from, map} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {VscodeInstallService} from '../../../services/vscode-install.service';
import {RouterLink} from '@angular/router';
import {ToastService} from '../../../services/toast.service';

type UsageMethod = 'cli' | 'vscode' | 'manual'

interface UsageDef {
  readonly method: UsageMethod
  readonly icon: IconType
  readonly label: string
}

const USAGE_METHODS: readonly UsageDef[] = [
  {method: 'vscode', icon: 'extension', label: 'VS Code'},
  {method: 'cli', icon: 'terminal', label: 'CLI'},
  {method: 'manual', icon: 'manual', label: 'Manual'}
]

interface DownloadInfo {
  readonly blob: Blob
  readonly url: string
}

@Component({
  selector: 'app-pack-usage-tab',
  imports: [
    ButtonComponent,
    IconComponent,
    AsyncPipe,
    RouterLink
  ],
  templateUrl: './pack-usage-tab.component.html',
  host: {class: 'app-column-fill gapped'}
})
export class PackUsageTabComponent {
  protected readonly detailsPage = inject(PackDetailsPageComponent)
  protected readonly pack = computed(() => this.detailsPage.pack())
  protected readonly packName = computed(() => this.pack()?.full_name)
  protected readonly methods = USAGE_METHODS
  protected readonly selectedUsage = signal<UsageDef>(this.methods[0]);
  protected readonly vscode = inject(VscodeInstallService)

  private readonly toast = inject(ToastService)

  protected readonly downloadFilename = computed(() => {
    const name = this.packName()
    return name ? `${name.replace('/', '_')}.md` : ''
  })

  protected readonly downloadBlob = this.detailsPage.previewContent().pipe(map(content => {
    if (!this.downloadInfo) {
      const blob = new Blob([content], {type: 'text/markdown;charset=utf-8'})
      this.downloadInfo = {
        blob,
        url: URL.createObjectURL(blob)
      }
    }
    return this.downloadInfo
  }))

  private downloadInfo?: DownloadInfo

  constructor(destroyRef: DestroyRef) {
    destroyRef.onDestroy(() => {
      if (this.downloadInfo) {
        URL.revokeObjectURL(this.downloadInfo.url)
        delete this.downloadInfo
      }
    })
  }

  protected async copyToClipboard({blob}: DownloadInfo) {
    this.toast.handleResult(
      'Copy to Clipboard',
      from(navigator.clipboard.writeText(await blob.text())),
      {
        successMessage: 'Successfully copied!',
      }
    )
  }
}
